import { Pagination, PaginationItem, PaginationLink } from "reactstrap";

const DOTS = "...";

/**
 * Gera um array de números em um intervalo.
 * @param {number} start - O número inicial.
 * @param {number} end - O número final.
 * @returns {number[]} - Array de números.
 */
const range = (start, end) => {
  let length = end - start + 1;
  return Array.from({ length }, (_, idx) => idx + start);
};

/**
 * Hook customizado para calcular a lógica da paginação.
 * @param {object} props
 * @param {number} props.totalCount - Total de itens.
 * @param {number} props.pageSize - Itens por página.
 * @param {number} props.siblingCount - Páginas vizinhas à página atual.
 * @param {number} props.currentPage - A página ativa.
 * @returns {(string|number)[]} - O array de itens de paginação.
 */
const usePagination = ({
  totalCount,
  pageSize,
  siblingCount = 1,
  currentPage,
}) => {
  const totalPageCount = Math.ceil(totalCount / pageSize);

  // Define a quantidade de números de página a serem exibidos
  const totalPageNumbers = siblingCount + 5;

  // Caso 1: Se o número de páginas for menor que os números que queremos mostrar,
  // retornamos o intervalo de [1..totalPageCount]
  if (totalPageNumbers >= totalPageCount) {
    return range(1, totalPageCount);
  }

  const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
  const rightSiblingIndex = Math.min(
    currentPage + siblingCount,
    totalPageCount
  );

  const shouldShowLeftDots = leftSiblingIndex > 2;
  const shouldShowRightDots = rightSiblingIndex < totalPageCount - 2;

  const firstPageIndex = 1;
  const lastPageIndex = totalPageCount;

  // Caso 2: Não mostrar reticências à esquerda, mas mostrar à direita
  if (!shouldShowLeftDots && shouldShowRightDots) {
    let leftItemCount = 3 + 2 * siblingCount;
    let leftRange = range(1, leftItemCount);
    return [...leftRange, DOTS, totalPageCount];
  }

  // Caso 3: Mostrar reticências à esquerda, mas não à direita
  if (shouldShowLeftDots && !shouldShowRightDots) {
    let rightItemCount = 3 + 2 * siblingCount;
    let rightRange = range(totalPageCount - rightItemCount + 1, totalPageCount);
    return [firstPageIndex, DOTS, ...rightRange];
  }

  // Caso 4: Mostrar reticências em ambos os lados
  if (shouldShowLeftDots && shouldShowRightDots) {
    let middleRange = range(leftSiblingIndex, rightSiblingIndex);
    return [firstPageIndex, DOTS, ...middleRange, DOTS, lastPageIndex];
  }
};

const PaginationComponent = (props) => {
  const {
    onPageChange,
    totalCount,
    siblingCount = 1,
    currentPage,
    pageSize,
    loading,
  } = props;

  const paginationRange = usePagination({
    currentPage,
    totalCount,
    siblingCount,
    pageSize,
  });

  // Se houver menos de 2 páginas, não renderizamos o componente
  if (currentPage === 0 || paginationRange.length < 2) {
    return null;
  }

  const onNext = () => {
    onPageChange(currentPage + 1);
  };

  const onPrevious = () => {
    onPageChange(currentPage - 1);
  };

  const onFirst = () => {
    onPageChange(1);
  };

  const onLast = () => {
    const lastPage = Math.ceil(totalCount / pageSize);
    onPageChange(lastPage);
  };

  const lastPage = paginationRange[paginationRange.length - 1];

  return (
    <Pagination listClassName="justify-content-center mt-3">
      {/* Botão Primeira Página */}
      <PaginationItem disabled={currentPage === 1 || loading}>
        <PaginationLink first onClick={onFirst} />
      </PaginationItem>
      {/* Botão Página Anterior */}
      <PaginationItem disabled={currentPage === 1 || loading}>
        <PaginationLink previous onClick={onPrevious} />
      </PaginationItem>

      {/* Números das Páginas */}
      {paginationRange.map((pageNumber, index) => {
        if (pageNumber === DOTS) {
          return (
            <PaginationItem key={`${DOTS}-${index}`} disabled>
              <PaginationLink>...</PaginationLink>
            </PaginationItem>
          );
        }

        return (
          <PaginationItem
            key={pageNumber}
            active={pageNumber === currentPage}
            disabled={loading}
          >
            <PaginationLink onClick={() => onPageChange(pageNumber)}>
              {pageNumber}
            </PaginationLink>
          </PaginationItem>
        );
      })}

      {/* Botão Próxima Página */}
      <PaginationItem disabled={currentPage === lastPage || loading}>
        <PaginationLink next onClick={onNext} />
      </PaginationItem>
      {/* Botão Última Página */}
      <PaginationItem disabled={currentPage === lastPage || loading}>
        <PaginationLink last onClick={onLast} />
      </PaginationItem>
    </Pagination>
  );
};

export default PaginationComponent;
